import { type ExportSheet, toCsv } from '@core/utils/tableExport';

const INVALID_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;
const MAX_SHEET_NAME_LENGTH = 31;
// Without the byte order mark, Excel opens UTF-8 CSVs as ANSI and garbles accented names.
const UTF8_BOM = '﻿';

const sanitizeSheetName = (name: string): string =>
  name.replace(INVALID_SHEET_NAME_CHARS, '').slice(0, MAX_SHEET_NAME_LENGTH);

const downloadBlob = (fileName: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

/** Builds a multi-sheet .xlsx workbook and triggers a browser download. */
export const downloadWorkbook = async (fileName: string, sheets: ExportSheet[]): Promise<void> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();

  for (const sheet of sheets) {
    const worksheet = workbook.addWorksheet(sanitizeSheetName(sheet.name));
    worksheet.addRow(sheet.headers).font = { bold: true };
    sheet.rows.forEach((row) => worksheet.addRow(row));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    fileName,
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
};

/** Serializes a single sheet as CSV and triggers a browser download. */
export const downloadCsv = (fileName: string, sheet: ExportSheet): void => {
  downloadBlob(fileName, new Blob([UTF8_BOM, toCsv(sheet)], { type: 'text/csv;charset=utf-8' }));
};
